import * as React from 'react';
import { connect } from 'react-redux';

import { default as Router, useRouter } from 'next/router';

import { default as ContainerDimensions } from 'react-container-dimensions';

import * as GeoJSON from 'geojson';

import {
  Button,
  createStyles,
  IconButton,
  Paper,
  Theme,
  useMediaQuery,
  useTheme,
  WithStyles,
  withStyles
} from '@material-ui/core';
import {
  AddRounded,
  FilterListRounded,
  LayersClearRounded,
  LocationOnRounded,
  RemoveRounded
} from '@material-ui/icons';

import classnames from 'classnames';
import {
  ExtraState,
  InteractiveMap,
  Layer as ReactMapGlLayer,
  Marker,
  PointerEvent,
  Popup,
  Source
} from 'react-map-gl';
import { default as WebMercatorViewport } from 'viewport-mercator-project';

import { ILieuucationMapProperties } from '@lieuu/core';

import * as GeolocatorActions from '@actions/GeolocatorActions';
import * as MapActions from '@actions/MapActions';
import * as SearchActions from '@actions/SearchActions';

import {
  IFilterState,
  IGeoLocatorState,
  IMapFilters,
  IMapState,
  ISearchState,
  IStore,
  IUserState,
  mapMaxZoom
} from '@reducers';

import { logEvent } from '@api/analytics';

import Loading from '@components/Loading';

import { default as defaultMapStyle } from '@assets/map-style/dark-map-style';
import fillDataLayer from '@assets/map-style/fill-data-layer';
import outlineDataLayer from '@assets/map-style/outline-data-layer';
import viewedDataLayer from '@assets/map-style/viewed-data-layer';
// import selectedDataLayer from '@assets/map-style/selected-data-layer';

import lieuucationCbsaLabelLayer from '@assets/map-style/lieuucation-cbsa-label-layer';
import lieuucationMicromarketLabelLayer from '@assets/map-style/lieuucation-micromarket-label-layer';
import lieuucationZipLabelLayer from '@assets/map-style/lieuucation-zip-label-layer';

import MapFilters from './MapFilters';
import MapPopup from './MapPopup';

import { bbox, Feature, featureCollection, Point } from '@turf/turf';
import { getCentroid } from './getCentroid';
import getRatingColor, { defaultColor } from './getRatingColor';
import { getNumericalScore } from './getScore';
import MapSearch from './MapSearch';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      height: `calc(100vh - ${theme.spacing(8)}px)`,
      width: '100%'
    },
    navigation: {
      position: 'absolute',
      top: theme.spacing(10),
      right: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      zIndex: 2
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row'
      },
      position: 'absolute',
      top: theme.spacing(10),
      left: theme.spacing(2),
      zIndex: 2
    },
    filterAndClearButton: {
      position: 'absolute',
      bottom: theme.spacing(2),
      right: theme.spacing(6),
      [theme.breakpoints.up('sm')]: {
        bottom: theme.spacing(4),
        right: theme.spacing(2)
      },
      zIndex: 50,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center'
    },
    filterOrClearButton: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      marginLeft: theme.spacing(2)
    },
    search: {
      width: 250,
      [theme.breakpoints.up('sm')]: {
        width: 500
      }
    },
    '@global': {
      '.mapboxgl-popup-content': {
        backgroundColor: `${theme.palette.background.paper} !important`
      },
      '.mapboxgl-popup-anchor-top .mapboxgl-popup-tip, .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip, .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip': {
        borderBottomColor: `${theme.palette.background.paper} !important`
      },
      '.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip, .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip, .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip': {
        borderTopColor: `${theme.palette.background.paper} !important`
      },
      '.mapboxgl-popup-anchor-left .mapboxgl-popup-tip': {
        borderRightColor: `${theme.palette.background.paper} !important`
      },
      '.mapboxgl-popup-anchor-right .mapboxgl-popup-tip': {
        borderLeftColor: `${theme.palette.background.paper} !important`
      }
    }
  });

const mapDispatchToProps = {
  zoomIn: MapActions.zoomIn,
  zoomOut: MapActions.zoomOut,
  onViewportChange: MapActions.onViewportChange,
  onFlyTo: MapActions.onFlyTo,
  getCurrentPosition: GeolocatorActions.getCurrentPosition.request,
  getPlaceForQuery: SearchActions.getPlaceForQuery.request
};

interface IState {
  filter: IFilterState;
  map: IMapState;
  geolocator: IGeoLocatorState;
  search: ISearchState;
  user: IUserState;
}

type IProps = IState & WithStyles<typeof styles> & typeof mapDispatchToProps;

interface IHoverInfo {
  lngLat: [number, number];
  properties: ILieuucationMapProperties;
}

const Map: React.FC<IProps> = ({
  onViewportChange,
  onFlyTo,
  getCurrentPosition,
  getPlaceForQuery,
  zoomIn,
  zoomOut,
  map,
  filter: { filters },
  search,
  classes,
  user
}) => {
  const router = useRouter();

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const mapEl = React.useRef<InteractiveMap>(null);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);

  const [hoverInfo, setHoverInfo] = React.useState<IHoverInfo | null>(null);

  const [searchPin, setSearchPin] = React.useState<{
    lon: number;
    lat: number;
  } | null>(null);

  const [isFilterShowing, setIsFilterShowing] = React.useState(false);

  const [centroidsAndFillPaint, setCentroidsAndFillPaint] = React.useState<{
    centroids: GeoJSON.FeatureCollection | undefined;
    paint: object;
  }>({ centroids: undefined, paint: {} });

  const [
    polygonAndBoundaryFilter,
    setPolygonAndBoundaryFilter
  ] = React.useState<{
    boundaryFilter: any[] | undefined; // tslint:disable-line
    polygon:
      | GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
      | undefined;
  }>({ boundaryFilter: undefined, polygon: undefined });

  const [viewedFilter, setViewedFilter] = React.useState<string[] | undefined>(
    undefined
  );

  const updateMapScores = (filters: IMapFilters) => {
    if (
      mapEl.current &&
      isMapLoaded &&
      filters &&
      mapEl.current.getMap().isSourceLoaded('lieuucations')
    ) {
      const features = mapEl.current
        .getMap()
        .querySourceFeatures('lieuucations', {
          sourceLayer: 'lieuucations'
        });

      const centroidPoints: Feature<
        Point,
        { name: string; type: string; id: string }
      >[] = [];

      const existingSet = new Set<String>();
      const colorStops: string[][] = [];

      features.forEach((e) => {
        const props = e.properties;

        if (props && props.id && !existingSet.has(props.id)) {
          const score = getNumericalScore(
            e.properties as ILieuucationMapProperties,
            filters
          );

          if (props.type === 'cbsa' || score > 0.6) {
            centroidPoints.push(
              getCentroid(props as ILieuucationMapProperties)
            );
          }

          colorStops.push([props.id, getRatingColor(score)]);
          existingSet.add(props.id);
        }
      });

      const allCentroids = featureCollection(
        centroidPoints
      ) as GeoJSON.FeatureCollection;

      setCentroidsAndFillPaint({
        centroids: allCentroids,
        paint: {
          ...fillDataLayer.paint,
          'fill-color': {
            stops: colorStops,
            type: 'categorical',
            property: 'id',
            default: defaultColor // added default to reduce "flashing" of colors when zooming/panning
          }
        }
      });
    }
  };

  React.useEffect(() => {
    // router.push(
    //   {
    //     pathname: '/map',
    //     query: {
    //       lat: map.viewport.latitude,
    //       lon: map.viewport.longitude,
    //       zoom: map.viewport.zoom
    //     }
    //   },
    //   undefined,
    //   { shallow: true }
    // );
  }, [map.viewport]);

  React.useEffect(() => {
    if (router.query) {
      if (router.query.zoom && router.query.lat && router.query.lon) {
        onViewportChange({
          zoom: Number(router.query.zoom),
          latitude: Number(router.query.lat),
          longitude: Number(router.query.lon)
        });
      } else if (router.query.query) {
        getPlaceForQuery(String(router.query.query));
      }
    } else {
      getCurrentPosition();
    }
  }, [router.query]);

  React.useEffect(() => {
    if (router.query.query && search.geocoderResult) {
      const { viewport } = search.geocoderResult.geometry;

      if (viewport) {
        const sw = viewport.getSouthWest();
        const ne = viewport.getNorthEast();

        const webViewport = new WebMercatorViewport({
          ...map.viewport
        });

        const fit = webViewport.fitBounds([
          [sw.lng(), sw.lat()],
          [ne.lng(), ne.lat()]
        ]);

        setSearchPin({
          lon: viewport.getCenter().lng(),
          lat: viewport.getCenter().lat()
        });

        onFlyTo({
          zoom: fit.zoom,
          latitude: fit.latitude,
          longitude: fit.longitude
        });
      }
    }
  }, [router.query && router.query.query, search.geocoderResult]);

  React.useEffect(() => {
    if (user.user && user.user.viewedLieuucations) {
      const viewedLieuucationIds = user.user.viewedLieuucations.map(
        (e) => e.internalId
      );

      setViewedFilter(['in', 'internalId', ...viewedLieuucationIds]);
    }
  }, [user.user && user.user.viewedLieuucations]);

  const getFeatureAtPoint = (point?: [number, number]) => {
    if (mapEl.current) {
      const features = mapEl.current.queryRenderedFeatures(point, {
        layers: ['lieuucation-fill']
      });

      if (features.length > 0 && features[0]) {
        return features[0];
      }
    }

    return null;
  };

  const getFeaturePropertiesAtPoint = (
    point?: [number, number]
  ): ILieuucationMapProperties | null => {
    const feature = getFeatureAtPoint(point);

    const id = feature && feature.properties && feature.properties.id;

    if (feature && id) {
      const properties: ILieuucationMapProperties = feature.properties as ILieuucationMapProperties;
      return properties;
    }

    return null;
  };

  React.useEffect(() => {
    if (mapEl.current && isMapLoaded) {
      updateMapScores(filters);
    }
  }, [filters, isMapLoaded]);

  React.useEffect(() => {
    if (mapEl.current && isMapLoaded) {
      const onRender = () => {
        updateMapScores(filters);
      };
      mapEl.current.getMap().on('render', onRender);
      return () => {
        if (mapEl.current) {
          mapEl.current.getMap().off('render', onRender);
        }
      };
    }

    return () => {};
  }, [isMapLoaded, filters]);

  const onClick = (e: PointerEvent) => {
    if (mapEl.current) {
      const feature = getFeatureAtPoint(e.point);

      if (feature && feature.properties) {
        const props = feature.properties as ILieuucationMapProperties;

        const type = props.type as 'lieuucation' | 'zip' | 'cbsa';

        if (feature.geometry['coordinates'] && mapEl.current) {
          const polygon: GeoJSON.Feature<
            GeoJSON.Polygon | GeoJSON.MultiPolygon
          > = feature as GeoJSON.Feature<
            GeoJSON.Polygon | GeoJSON.MultiPolygon
          >;

          const bounding = bbox(polygon);

          let fit: WebMercatorViewport | null = null;

          try {
            const webViewport = new WebMercatorViewport({
              ...map.viewport
            });

            fit = webViewport.fitBounds([
              [bounding[0], bounding[1]],
              [bounding[2], bounding[3]]
            ]);
          } catch (e) {
            console.error(e);
          }

          const zoomTo = {
            zoom:
              fit && fit.zoom
                ? Math.round(fit.zoom * 100) / 100
                : type === 'lieuucation'
                ? 12
                : type === 'zip'
                ? 9.5
                : 5.5,
            lat:
              Math.round(
                ((fit && fit.latitude) || props.centLat || e.lngLat[1]) * 1000
              ) / 1000,
            lon:
              Math.round(
                ((fit && fit.longitude) || props.centLon || e.lngLat[0]) * 1000
              ) / 1000
          };

          if (type === 'lieuucation') {
            // go to report
            Router.push({
              pathname: `/micromarket/${props.internalId}`
            });
          } else {
            if (props.name) {
              // tslint:disable-next-line
              const boundaryFilter: any[] = [
                'any',

                ['==', ['get', String(type)], String(props.name)],

                ['==', ['get', 'id'], String(props.id)]
              ];

              if (props.cbsa) {
                boundaryFilter.push([
                  'all',
                  ['==', ['get', 'name'], String(props.cbsa)],
                  ['==', ['get', 'type'], 'cbsa']
                ]);
              }

              if (props.zip) {
                boundaryFilter.push([
                  'all',
                  ['==', ['get', 'name'], String(props.zip)],
                  ['==', ['get', 'type'], 'zip']
                ]);
              }
              setPolygonAndBoundaryFilter({
                boundaryFilter,
                polygon
              });

              onFlyTo({
                zoom: zoomTo.zoom,
                latitude: zoomTo.lat,
                longitude: zoomTo.lon
              });
            }
          }
        }
      }
    }
  };

  const getCursor = ({ isHovering, isDragging }: ExtraState) => {
    return isHovering ? 'pointer' : isDragging ? 'grab' : 'default';
  };

  const onHover = (e: PointerEvent) => {
    if (!isSmall) {
      const featurePropertiesAtPoint = getFeaturePropertiesAtPoint(e.point);
      if (featurePropertiesAtPoint) {
        setHoverInfo({
          properties: featurePropertiesAtPoint,
          lngLat: e && e.lngLat
        });
      }
    }
  };

  const filter = polygonAndBoundaryFilter.boundaryFilter
    ? { filter: polygonAndBoundaryFilter.boundaryFilter }
    : {};

  return false ? (
    <Loading />
  ) : (
    <ContainerDimensions>
      {({ width, height }) => {
        return (
          <div className={classnames('mapbox-map', classes.container)}>
            <Paper className={classes.navigation}>
              <IconButton onClick={() => zoomIn()} aria-label="Zoom In">
                <AddRounded />
              </IconButton>
              <IconButton onClick={() => zoomOut()} aria-label="Zoom Out">
                <RemoveRounded />
              </IconButton>
            </Paper>

            <MapFilters
              isOpen={isFilterShowing}
              onClose={() => setIsFilterShowing(false)}
            />
            <div className={classes.filterAndClearButton}>
              {polygonAndBoundaryFilter.boundaryFilter && (
                <Button
                  className={classes.filterOrClearButton}
                  size="large"
                  variant="contained"
                  color="default"
                  startIcon={<LayersClearRounded />}
                  onClick={() => {
                    logEvent({ category: 'Map', action: 'Boundary Cleared' });
                    setPolygonAndBoundaryFilter({
                      boundaryFilter: undefined,
                      polygon: undefined
                    });
                  }}
                  aria-label="Filters"
                >
                  Clear
                </Button>
              )}

              <Button
                className={classes.filterOrClearButton}
                size="large"
                variant="contained"
                color="default"
                startIcon={<FilterListRounded />}
                onClick={() => {
                  logEvent({ category: 'Map', action: 'Filters Opened' });
                  setIsFilterShowing(!isFilterShowing);
                }}
                aria-label="Filters"
              >
                Filters
              </Button>
            </div>

            <div className={classes.searchContainer}>
              <Paper className={classes.search}>
                <MapSearch
                  location={{
                    latitude: map.viewport.latitude,
                    longitude: map.viewport.longitude
                  }}
                />
              </Paper>
            </div>

            <InteractiveMap
              minZoom={3}
              maxZoom={mapMaxZoom}
              ref={mapEl}
              onMouseOut={() => setHoverInfo(null)}
              onMouseLeave={() => setHoverInfo(null)}
              onClick={onClick}
              onLoad={() => setIsMapLoaded(true)}
              onHover={onHover}
              onResize={(e) =>
                onViewportChange({ width: e.width, height: e.height })
              }
              getCursor={getCursor}
              onViewportChange={onViewportChange}
              mapboxApiAccessToken={process.env.MAPBOX_PUBLIC_ACCESS_TOKEN}
              mapStyle={defaultMapStyle}
              interactiveLayerIds={['lieuucation-fill']}
              {...map.viewport}
              width={width || '100%'}
              height={height || '100%'}
            >
              {!isMapLoaded && <Loading />}

              {searchPin && (
                <Marker
                  longitude={searchPin.lon}
                  latitude={searchPin.lat}
                  offsetLeft={-12}
                  offsetTop={-16}
                >
                  <LocationOnRounded fontSize="large" color="secondary" />
                </Marker>
              )}

              {hoverInfo && (
                <Popup
                  // className={classes.popup}
                  longitude={hoverInfo.lngLat[0]}
                  latitude={hoverInfo.lngLat[1]}
                  closeButton={false}
                >
                  <MapPopup
                    properties={hoverInfo.properties}
                    filters={filters}
                  />
                </Popup>
              )}

              {/* {polygonAndBoundaryFilter.polygon && (
                <Source type="geojson" data={polygonAndBoundaryFilter.polygon}>
                  <ReactMapGlLayer {...selectedDataLayer} />
                </Source>
              )} */}
              {centroidsAndFillPaint.centroids && (
                <Source type="geojson" data={centroidsAndFillPaint.centroids}>
                  <ReactMapGlLayer {...lieuucationZipLabelLayer} />
                  <ReactMapGlLayer {...lieuucationCbsaLabelLayer} />
                  <ReactMapGlLayer {...lieuucationMicromarketLabelLayer} />
                </Source>
              )}
              {viewedFilter && (
                <ReactMapGlLayer {...viewedDataLayer} filter={viewedFilter} />
              )}
              <ReactMapGlLayer {...outlineDataLayer} />
              <ReactMapGlLayer
                {...fillDataLayer}
                {...filter}
                paint={centroidsAndFillPaint.paint}
              />
            </InteractiveMap>
          </div>
        );
      }}
    </ContainerDimensions>
  );
};

export default withStyles(styles)(
  connect(
    (state: IStore): IState => ({
      filter: state.filter,
      map: state.map,
      geolocator: state.geolocator,
      search: state.search,
      user: state.user
    }),
    mapDispatchToProps
  )(Map)
);
